<template>
    <v-card
        flat
        elevation="0"
        class="bible-form"
        :loading="loading"
    >
        <v-card-title>
            <span class="header2">{{ $t('data_gatherings.generate_form.title') }}</span>
        </v-card-title>
        <v-card-text>
            <v-form
                @submit.prevent="save"
                :disabled="loading"
                v-model="flags.form_valid"
                ref="form"
            >
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <span class="label">{{ $t('data_gatherings.title') }}:</span>
                            <v-text-field
                                v-model="data_gathering.title"
                                :rules="data_gathering_rules.title"
                                class="input-text input-text--text-field body-text"
                                dense
                                solo
                                flat
                                outlined
                                required
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12">
                            <span class="label">{{ $t('data_gatherings.notes') }}:</span>
                            <v-textarea
                                v-model="data_gathering.notes"
                                solo
                                flat
                                outlined
                                required
                                class="input-text body-text"
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <span class="label">{{ $t('data_gatherings.academic_period') }}:</span>
                            <v-select
                                class="input-text input-text--select body-text"
                                v-model="data_gathering.academic_period.id"
                                :rules="data_gathering_rules.academic_period"
                                dense
                                solo
                                flat
                                attach
                                outlined
                                :items="academicPeriods"
                                required
                                item-text="title"
                                item-value="id"
                                hide-details="auto"
                                menu-props="top, overflowY"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <span class="label">{{ $t('data_gatherings.type') }}:</span>
                            <v-select
                                class="input-text input-text--select body-text"
                                v-model="data_gathering.type.id"
                                :rules="data_gathering_rules.data_type"
                                dense
                                solo
                                flat
                                attach
                                :menu-props="{ top: true, right: true }"
                                outlined
                                :items="types"
                                required
                                item-text="title"
                                item-value="id"
                                :item-disabled="item => isItemDisabled(item)"
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <span class="label">{{ $t('data_gatherings.strategy') }}:</span>
                            <v-select
                                class="input-text input-text--select body-text"
                                :disabled="strategies.length < 0"
                                v-model="data_gathering.strategy.id"
                                :rules="data_gathering_rules.data_strategy"
                                dense
                                solo
                                flat
                                outlined
                                :items="strategies"
                                required
                                item-text="title"
                                item-value="id"
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <span class="label">{{ $t('data_gatherings.mode') }}:</span>
                            <v-select
                                class="input-text input-text--select body-text"
                                :disabled="availableModes.length < 0"
                                v-model="data_gathering.mode"
                                :rules="data_gathering_rules.mode"
                                dense
                                solo
                                flat
                                outlined
                                :items="availableModes"
                                required
                                item-text="title"
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12" v-if="data_gathering.mode === 'manual'">
                            <span class="label">{{ $t('data_gatherings.import') }}</span>
                            <br />
                            <div class="upload-container" v-if="!spreadSheetSelected">
                                <drag-and-drop-file-picker
                                    @select="this.uploadFile"
                                />

<!--                                <div class="o-divider">-->
<!--                                    <v-spacer />-->
<!--                                    <div class="o-partition">o</div>-->
<!--                                    <v-spacer />-->
<!--                                </div>-->

<!--                                <google-drive-file-picker-->
<!--                                    :disabled="!this.googleDrive"-->
<!--                                />-->
                            </div>
                            <div class="file-container" v-if="spreadSheetSelected">
                                <v-card v-if="spreadSheetData">
                                    <div class="file-card-icon">
                                        <v-icon class="check align-middle">mdi-file-excel</v-icon>
                                    </div>
                                    <div class="text-cont">
                                        <v-card-title class="body-text">
                                            {{ spreadSheetData.file_name }}
                                        </v-card-title>
                                        <v-card-subtitle class="small-body">
                                            {{ spreadSheetData.size | toHumanSize }}
                                        </v-card-subtitle>
                                    </div>
                                    <v-spacer />
                                    <v-spacer />
                                    <v-spacer />
                                    <v-btn @click="removeSpreadSheet" class="btn-icon btn-icon--delete">
                                        <v-icon>mdi-trash-can</v-icon>
                                    </v-btn>
                                </v-card>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-btn
                class="v-btn--secondary"
                :loading="loading"
                @click="cancel"
            >
                {{ $t('data_gatherings.cancel') }}
            </v-btn>
            <v-btn
                class="v-btn--primary"
                :loading="loading"

                @click="save"
            >
                {{ $t('data_gatherings.generate') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import {mapState, mapActions} from 'pinia';
import _ from "lodash";
import {DataGatheringsStore} from "@/Store/DataGatherings/DataGatheringsStore";
import DataGatheringRepository from "@/Repositories/DataGathering/DataGatheringRepository";
import toastMixin from "@/Shared/toastMixin";
import StrategySelectionMixin from "@/Shared/DathaGatherings/Common/Forms/StrategySelectionMixin";
import {bibleResumeAbbr} from "@/Repositories/DataGathering/DataGatheringRepository";

/**
 * All flags used in this component, this will be used as a factory function
 *
 * @returns {{saving: boolean, form_valid: boolean, spreadSheetSelected: boolean, file_loading: boolean}}
 */
const buildFlags = () => ({
    saving: false,
    file_loading: false,
    form_valid: false,
    spreadSheetSelected: false,
});

export default {
    name: "GenerateForm",

    mixins: [StrategySelectionMixin, toastMixin],

    props: {

    },

    data: () => ({
        flags: buildFlags(),
    }),

    methods: {
        // Disable resume bibles in the type select
        isItemDisabled(item){
            return bibleResumeAbbr.inArray(item.abbr);
        },

        save() {
            if(!this.formValid){
                return;
            }

            this.flags.saving = true;

            //set created by info
            const user = _.get(this.$page.props, 'auth');
            this.data_gathering.setCreatedBy(user);

            this.store(this.data_gathering)
                .then(data_gathering => {
                    this.$emit('save', data_gathering);

                    this.reset();

                    this.toast().success(
                        this.$t('data_gatherings.generate_form.success_store_title', {data_gathering: data_gathering.title}),
                        this.$t('data_gatherings.generate_form.success_store_message'),
                    );
                })
                .catch(error => {
                    this.toast().error(
                        this.$t('data_gatherings.generate_form.error_store_title'),
                        this.$t('data_gatherings.generate_form.error_store_message'),
                    );

                    console.error('data-gathering.store.error', error, _.cloneDeep(this.data_gathering));
                })
                .finally(() => this.flags.saving = false);
        },

        async cancel() {
            this.flags.saving = true;

            await this.removeSpreadSheet();

            this.reset();

            this.$emit('cancel');
        },

        reset() {
            this.flags = buildFlags();

            this.data_gathering = DataGatheringRepository.build();

            this.spreadSheetData = {
                file_name: null,
                size: null,
            };

            this.$refs.form.resetValidation();
        },

        ...mapActions(DataGatheringsStore, [
            'store'
        ])
    },

    computed: {
        loading() {
            return this.flags.saving || this.flags.file_loading;
        },

        formValid() {
            return this.flags.form_valid && !!this.spreadSheetData.file_name;
        },
    },
}
</script>

<style lang="scss" scoped>

::v-deep .v-menu__content {
    max-height: 204px !important;
}

</style>
